import { combineReducers } from 'redux';

import type { Action, PayloadAction } from 'modules/global/generic.types';
import createSearchReducers from 'modules/global/reducers/simpleSearchReducer';
import { FETCH_GUIDELINES, FIND_GUIDELINES_DEALER_OR_GROUP } from 'modules/guidelines/actionTypes';
import { DEALER_DASHBOARD_SOURCE, GROUP_DASHBOARD_SOURCE } from 'modules/guidelines/constants';
import { createReducer } from 'utils/ReduxUtils';

import filters from './filters';

const isFromSource = (source: string) => ({ meta }: Action) => meta?.source === source;

type GuidelineSliceOptions = {
  // Restricts the slice to actions carrying this meta.source. Required when several
  // slices share the same action root (group/dealer both listen to FETCH_GUIDELINES).
  source?: string;
  defaultSort?: string | string[];
  // `paginationSlice` controls how `ids` reacts to a fetch success:
  //  - true  → each page REPLACES the list (classic paginated table: group/dealer dashboards).
  //  - false → each page is APPENDED, the list resets only on a fresh (page 0) request
  //            (infinite-scroll select: config / Dealer Config > Sales > Guideline).
  paginationSlice?: boolean;
};

// Each guideline list (group / dealer / config) is a paginated search slice
// (ids / loading / error / page / hasMore) plus a `sort` reducer fed by the request params.
const createGuidelineReducer = (
  actionRoot: string,
  { source, defaultSort = 'name,desc', paginationSlice = true }: GuidelineSliceOptions
) =>
  createSearchReducers(
    actionRoot,
    {
      global: {
        paginationSlice,
        ...(source ? { matcher: isFromSource(source) } : {}),
        // Annotated, not passed as a type argument: naming `T` explicitly would stop TypeScript
        // inferring the additional reducers below (no partial type-argument inference).
        selectId: ({ guidelineId }: { guidelineId: string }) => guidelineId,
      },
    },
    {
      sort: createReducer<string | string[]>(defaultSort, {
        [actionRoot]: (state, action: PayloadAction<{ request: { params: { sort?: string | string[] } } }>) =>
          (source && action.meta?.source !== source
            ? state
            : action.payload.request.params.sort ?? state),
      }),
    }
  );

export default combineReducers({
  filters,
  group: createGuidelineReducer(FETCH_GUIDELINES, { source: GROUP_DASHBOARD_SOURCE }),
  dealer: createGuidelineReducer(FETCH_GUIDELINES, { source: DEALER_DASHBOARD_SOURCE }),
  // Infinite-scroll select: pages must accumulate, not replace (BMWVID-28466).
  config: createGuidelineReducer(FIND_GUIDELINES_DEALER_OR_GROUP, {
    defaultSort: ['dealerId,asc', 'groupId,asc'],
    paginationSlice: false,
  }),
});
